import React from "react";
import { keyframes } from "@emotion/react";
import { Box, VStack, Heading, Text, HStack, SimpleGrid, Link, Button, TableContainer, Table, Thead, Tbody, Tr, Th, Td, usePrefersReducedMotion } from "@chakra-ui/react";

const howItWorksFlow = ["Check eligibility", "Submit application", "Get cash"];

const flowMarquee = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const whySwiftCreditPoints = [
  {
    icon: "🛡️",
    title: "Bad credit lending",
    desc: "At Swift Credit, we believe every Canadian deserves a fair opportunity to rebuild their credit, and past credit challenges do not automatically disqualify an application. We accept non-traditional income sources, including gig and self-employed earnings.",
  },
  {
    icon: "🔎",
    title: "Transparent, competitive pricing",
    desc: "Rates, fees, and repayment expectations are disclosed before you accept, with pricing designed to remain among the most competitive in Canada.",
  },
  {
    icon: "✅",
    title: "Responsible lending",
    desc: "Every application includes affordability and suitability checks to support sustainable borrowing decisions and better serve our customers.",
  },
  {
    icon: "⚡",
    title: "Easy application and fast approvals",
    desc: "Complete a simple application in minutes and get a quick approval decision for eligible requests in minutes, not days.",
  },
  {
    icon: "📅",
    title: "Repayment options",
    desc: "Choose schedules that better match your cashflow and income cycle.",
  },
  {
    icon: "🔐",
    title: "Your privacy matters",
    desc: "Secure online transactions and protected data handling help keep your personal and financial information safe.",
  },
];

const comparisonRows = [
  { feature: "Bad Credit Loans", swiftCredit: "✓", traditionalLenders: "✓", bigBanks: "" },
  { feature: "Non-employment Income Accepted", swiftCredit: "✓", traditionalLenders: "", bigBanks: "✓" },
  { feature: "5 Minute Application", swiftCredit: "✓", traditionalLenders: "", bigBanks: "" },
  { feature: "Instant direct deposit", swiftCredit: "✓", traditionalLenders: "", bigBanks: "" },
  { feature: "No Collateral Needed", swiftCredit: "✓", traditionalLenders: "✓", bigBanks: "" },
  { feature: "Intuitive Loan Calculator", swiftCredit: "✓", traditionalLenders: "✓", bigBanks: "" },
  { feature: "Available 24/7", swiftCredit: "✓", traditionalLenders: "", bigBanks: "" },
  { feature: "Fixed Interest Rates", swiftCredit: "✓", traditionalLenders: "✓", bigBanks: "✓" },
  { feature: "No Repayment Penalty", swiftCredit: "✓", traditionalLenders: "✓", bigBanks: "" },
  { feature: "100% Privacy Guaranteed", swiftCredit: "✓", traditionalLenders: "✓", bigBanks: "✓" },
];


export default function LendingOverviewSections() {
  const prefersReducedMotion = usePrefersReducedMotion();

  const scrollToLoanCalculator = () => {
    const calculatorSection = document.getElementById("loan-calculator");
    if (calculatorSection) {
      const headerOffset = 88;
      const calculatorTop = calculatorSection.getBoundingClientRect().top + window.pageYOffset;
      const targetPosition = Math.max(calculatorTop - headerOffset, 0);
      window.scrollTo({ top: targetPosition, behavior: "smooth" });
    }
  };

  return (
    <Box width="100%" py={{ base: 12, md: 16 }} bg="white">
      <VStack spacing={10} align="stretch" width="100%">
        <Box px={{ base: 4, md: 8 }}>
          <VStack align="stretch" spacing={5} maxW="1100px" mx="auto">
          <VStack spacing={2} align="center">
            <Heading size="lg" textAlign="center">Why Swift Credit?</Heading>
            <Text color="gray.600" textAlign="center" maxW="760px">
              Built for modern earners with flexible approvals, clear pricing, and repayment options that fit real cashflow.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {whySwiftCreditPoints.map((point) => (
              <Box
                key={point.title}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                p={6}
                bg="white"
                textAlign="center"
                transition="all 0.2s ease"
                _hover={{ transform: "translateY(-2px)", boxShadow: "md", borderColor: "black" }}
                _focusWithin={{ borderColor: "black", boxShadow: "outline" }}
              >
                <Box
                  mx="auto"
                  mb={3}
                  w="52px"
                  h="52px"
                  borderRadius="full"
                  bg="blue.50"
                  border="1px solid"
                  borderColor="blue.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="24px"
                >
                  {point.icon}
                </Box>
                <Text fontWeight="700" mb={2}>{point.title}</Text>
                <Text
                  color="gray.600"
                  fontSize="sm"
                  textAlign="justify"
                  textJustify="inter-word"
                  hyphens="auto"
                >
                  {point.desc}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          <HStack spacing={3} align="center" justify="center" pt={8}>
            <Box flex="1" h="1px" bg="gray.200" />
            <Box
              px={4}
              py={1}
              border="1px solid"
              borderColor="gray.800"
              borderRadius="full"
              bg="gray.800"
            >
              <HStack spacing={2} align="center">
                <Text fontSize="xs" color="blue.600" lineHeight="1">✦</Text>
                <Text fontSize="xs" fontWeight="700" color="white" letterSpacing="0.4px" textTransform="uppercase">
                  The Swift Credit advantage
                </Text>
              </HStack>
            </Box>
            <Box flex="1" h="1px" bg="gray.200" />
          </HStack>

          <TableContainer border="1px solid" borderColor="gray.200" borderRadius="xl" bg="white" boxShadow="sm" mt={2}>
            <Table variant="simple" size="md">
              <Thead bg="gray.50">
                <Tr>
                  <Th minW="280px"></Th>
                  <Th textAlign="center">Swift Credit</Th>
                  <Th textAlign="center">Traditional Personal Lenders</Th>
                  <Th textAlign="center">Big Banks</Th>
                </Tr>
              </Thead>
              <Tbody>
                {comparisonRows.map((row, index) => (
                  <Tr key={row.feature} bg={index % 2 === 0 ? "white" : "blue.50"}>
                    <Td fontWeight="600">{row.feature}</Td>
                    <Td textAlign="center" color="green.600" fontWeight="700" fontSize="lg">{row.swiftCredit}</Td>
                    <Td textAlign="center" color="gray.700">{row.traditionalLenders}</Td>
                    <Td textAlign="center" color="gray.700">{row.bigBanks}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          </VStack>
        </Box>

        <Box
          id="how-it-works"
          scrollMarginTop={{ base: "90px", md: "110px" }}
          bg="gray.50"
          w="100%"
          py={{ base: 10, md: 14 }}
        >
          <VStack spacing={8} maxW="1100px" mx="auto" px={{ base: 4, md: 8 }} align="stretch" minH="100vh" justify="center">
            <Heading size="lg" textAlign="center">How it works</Heading>

            <Box
              border="1px solid"
              borderColor="blue.100"
              borderRadius="xl"
              p={{ base: 4, md: 6 }}
              bg="blue.50"
              boxShadow="sm"
            >
              <Box
                display={{ base: "block", md: "none" }}
                overflowX={prefersReducedMotion ? "auto" : "hidden"}
                w="100%"
                sx={{ WebkitOverflowScrolling: "touch" }}
                scrollBehavior="smooth"
              >
                <HStack
                  spacing={2}
                  justify="flex-start"
                  align="center"
                  flexWrap="nowrap"
                  minW="max-content"
                  px={1}
                  animation={prefersReducedMotion ? undefined : `${flowMarquee} 14s linear infinite`}
                >
                  {[0, 1].map((loop) => (
                    <HStack key={loop} spacing={2} flexShrink={0} pr={2}>
                      {howItWorksFlow.map((step, index) => (
                        <React.Fragment key={`${loop}-${step}`}>
                          <Box
                            px={4}
                            py={2}
                            border="1px solid"
                            borderColor="gray.300"
                            borderRadius="full"
                            bg="white"
                            minW="170px"
                            textAlign="center"
                            flexShrink={0}
                          >
                            <Text fontWeight="700" color="gray.800">{step}</Text>
                          </Box>
                          {index < howItWorksFlow.length - 1 && (
                            <Text fontSize="xl" color="gray.500" fontWeight="700" px={1} flexShrink={0}>
                              →
                            </Text>
                          )}
                        </React.Fragment>
                      ))}
                    </HStack>
                  ))}
                </HStack>
              </Box>

              <Box display={{ base: "none", md: "block" }} w="100%">
                <HStack spacing={4} justify="center" align="center" flexWrap="nowrap">
                  {howItWorksFlow.map((step, index) => (
                    <React.Fragment key={`desktop-${step}`}>
                      <Box
                        px={5}
                        py={3}
                        border="1px solid"
                        borderColor="gray.300"
                        borderRadius="full"
                        bg="white"
                        minW="210px"
                        textAlign="center"
                      >
                        <Text fontWeight="700" color="gray.800">{step}</Text>
                      </Box>
                      {index < howItWorksFlow.length - 1 && (
                        <Text fontSize="xl" color="gray.500" fontWeight="700" px={1}>
                          →
                        </Text>
                      )}
                    </React.Fragment>
                  ))}
                </HStack>
              </Box>
          </Box>

            <VStack align="center" spacing={5}>
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                p={{ base: 5, md: 6 }}
                pb={{ base: 12, md: 14 }}
                bg="white"
                w="100%"
                maxW="980px"
                textAlign="center"
                position="relative"
              >
              <VStack spacing={3} mb={3}>
                <Box
                  w="48px"
                  h="48px"
                  borderRadius="full"
                  bg="white"
                  color="black"
                  border="2px solid"
                  borderColor="black"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="800"
                  fontSize="lg"
                  boxShadow="sm"
                >
                  1
                </Box>
                <Heading size="md">Check eligibility</Heading>
              </VStack>
              <Text
                color="gray.700"
                mb={4}
                maxW="860px"
                mx="auto"
                textAlign="justify"
                textJustify="inter-word"
                hyphens="auto"
              >
                Swift Credit has only two eligibility requirements, unlike banks and many {/*payday*/} lenders. If your answer is yes to the questions below, you are eligible to apply.
              </Text>
              <VStack align="stretch" spacing={2} mb={3} w="100%" maxW="860px" mx="auto">
                <Text color="gray.700" textAlign="justify" textJustify="inter-word" hyphens="auto">
                  1. Have I paid off my {/*payday*/} loans in the past?
                </Text>
                <Text color="gray.700" textAlign="justify" textJustify="inter-word" hyphens="auto">
                  2. Do I have a fixed monthly income deposited directly into my bank account?
                </Text>
              </VStack>
              <Link href="/eligibility" color="blue.600" fontWeight="600">See acceptable income</Link>
            </Box>

              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                p={{ base: 5, md: 6 }}
                pb={{ base: 12, md: 14 }}
                bg="white"
                w="100%"
                maxW="980px"
                textAlign="center"
                position="relative"
              >
              <VStack spacing={3} mb={3}>
                <Box
                  w="48px"
                  h="48px"
                  borderRadius="full"
                  bg="blue.50"
                  color="black"
                  border="1px solid"
                  borderColor="black"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="800"
                  fontSize="lg"
                  boxShadow="sm"
                >
                  2
                </Box>
                <Heading size="md">Submit application</Heading>
              </VStack>
              <Text
                color="gray.700"
                mb={3}
                maxW="860px"
                mx="auto"
                textAlign="justify"
                textJustify="inter-word"
                hyphens="auto"
              >
                Our instant loan application is straightforward and can be completed in a few minutes.
              </Text>
              <Text
                color="gray.700"
                mb={3}
                maxW="860px"
                mx="auto"
                textAlign="justify"
                textJustify="inter-word"
                hyphens="auto"
              >
                If you are unsure about repayment amounts and interest, use our loan calculator before submitting.
              </Text>
              <Text
                color="gray.700"
                maxW="860px"
                mx="auto"
                textAlign="justify"
                textJustify="inter-word"
                hyphens="auto"
              >
                After submission, Swift Credit evaluates your application and may use additional information from Equifax Canada. We will send a confirmation decision within a few hours.
              </Text>
              <HStack justify="center" mt={5}>
                <Button
                  onClick={scrollToLoanCalculator}
                  bg="gray.700"
                  color="white"
                  borderRadius="full"
                  px={7}
                  _hover={{ bg: "gray.800" }}
                >
                  Loan calculator
                </Button>
              </HStack>
            </Box>

              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                p={{ base: 5, md: 6 }}
                pb={{ base: 12, md: 14 }}
                bg="white"
                w="100%"
                maxW="980px"
                textAlign="center"
                position="relative"
              >
              <VStack spacing={3} mb={3}>
                <Box
                  w="48px"
                  h="48px"
                  borderRadius="full"
                  bg="black"
                  color="white"
                  border="2px solid"
                  borderColor="white"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="800"
                  fontSize="lg"
                  boxShadow="0 0 0 2px"
                >
                  3
                </Box>
                <Heading size="md">Get cash</Heading>
              </VStack>
              <Text color="gray.700" mb={0}>
                Once approved, your funds are deposited directly into your bank account instantly.
              </Text>
              <Button
                as="a"
                href="/apply"
                bg="black"
                color="white"
                borderRadius="full"
                minW={{ base: "220px", md: "300px" }}
                px={10}
                h="50px"
                position="absolute"
                left="50%"
                bottom="0"
                transform="translate(-50%, 50%)"
                _hover={{ bg: "gray.900" }}
              >
                Apply now
              </Button>
              </Box>
            </VStack>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
